import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, MapPin, Phone, Award, Heart, Users, Star, Calendar, Clock, Target, Shield, ChevronRight } from 'lucide-react';

const AgenziaImmobiliareScandicci = () => {
  return (
    <div className="bg-gray-50">
      {/* ATTENTION: Hero Section - cattura l'attenzione con un forte impatto visivo */}
      <section className="relative pt-28 pb-20 bg-gradient-to-r from-primary to-primary-dark overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" 
            alt="Scandicci aerial view" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-primary/70"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block px-4 py-2 bg-secondary/20 backdrop-blur-sm text-white text-sm font-bold rounded-full mb-6">
              L'Agenzia Immobiliare di Riferimento a Scandicci dal 2017
            </span>
            <h1 className="text-5xl md:text-6xl font-monomakh text-white mb-6 leading-tight">
              La Tua Casa dei Sogni <br />Ti Aspetta a Scandicci
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Molinaro Immobiliare trasforma la ricerca della casa ideale in un'esperienza semplice, trasparente e appagante.
              Con una profonda conoscenza del territorio, valorizziamo ogni immobile e ogni cliente con passione e professionalità.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <div className="bg-white/10 backdrop-blur-sm px-5 py-3 rounded-lg flex items-center">
                <Building2 className="h-5 w-5 text-secondary mr-3" />
                <span className="text-white">500+ immobili venduti</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-5 py-3 rounded-lg flex items-center">
                <Award className="h-5 w-5 text-secondary mr-3" />
                <span className="text-white">Esperti del territorio</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-5 py-3 rounded-lg flex items-center">
                <Heart className="h-5 w-5 text-secondary mr-3" />
                <span className="text-white">Consulenza personalizzata</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="#contattaci"
                className="bg-secondary hover:bg-secondary-light text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
              >
                Richiedi Consulenza Gratuita
                <ChevronRight className="ml-2 h-5 w-5" />
              </a>
              <Link
                to="/immobili"
                className="bg-white text-primary hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
              >
                Scopri i Nostri Immobili
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* INTEREST: Expertise Section - crea interesse con dati e benefici concreti */}
      <section className="py-24 bg-white relative">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="text-secondary uppercase tracking-wider font-semibold">Esperti del Territorio</span>
            <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">
              Perché Scegliere Molinaro Immobiliare a Scandicci
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              Dal 2017 conosciamo ogni angolo di Scandicci e le sue dinamiche immobiliari.
              La nostra esperienza è la tua garanzia per decisioni immobiliari sicure e vantaggiose.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all border-t-4 border-primary"
            >
              <MapPin className="h-12 w-12 text-primary mb-6" />
              <h3 className="text-xl font-semibold text-primary mb-4">Specialisti di Scandicci</h3>
              <p className="text-gray-600 mb-4">
                Conosciamo ogni zona di Scandicci nei minimi dettagli: prezzi, servizi, trasporti, scuole e tendenze di mercato.
                Questa conoscenza territoriale ci permette di consigliarti le migliori soluzioni per le tue esigenze.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Analisi dettagliata delle zone</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Valutazioni realistiche di mercato</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Consulenza territoriale personalizzata</span>
                </li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all border-t-4 border-secondary"
            >
              <Target className="h-12 w-12 text-secondary mb-6" />
              <h3 className="text-xl font-semibold text-secondary mb-4">Marketing Strategico</h3>
              <p className="text-gray-600 mb-4">
                Strategie di marketing avanzate per vendere il tuo immobile al miglior prezzo e nei tempi più brevi.
                Ogni immobile riceve un piano personalizzato di valorizzazione e promozione.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Servizi fotografici professionali</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Annunci ottimizzati sui portali</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Promozione sui social media</span>
                </li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all border-t-4 border-primary"
            >
              <Shield className="h-12 w-12 text-primary mb-6" />
              <h3 className="text-xl font-semibold text-primary mb-4">Assistenza Completa</h3>
              <p className="text-gray-600 mb-4">
                Ti accompagniamo in ogni fase della compravendita, dalla prima visita al rogito,
                gestendo tutti gli aspetti burocratici e coordinando i professionisti coinvolti.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Consulenza legale e fiscale</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Assistenza per mutui e finanziamenti</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Coordinamento fino al rogito</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* DESIRE: Success Metrics - crea desiderio mostrando i risultati e il valore */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 rounded-br-full bg-primary/5"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 rounded-tl-full bg-secondary/5"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="text-secondary uppercase tracking-wider font-semibold">I Nostri Risultati</span>
            <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">
              Numeri che Raccontano la Nostra Storia
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              Dal 2017 contribuiamo a realizzare i sogni immobiliari di centinaia di famiglie a Scandicci.
              La nostra esperienza si traduce in risultati concreti e misurabili.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <Calendar className="h-12 w-12 text-secondary mx-auto mb-4" />
                <div className="text-4xl font-bold text-primary mb-2">2017</div>
                <p className="text-gray-600">Anno di fondazione</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-center"
            >
              <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <Building2 className="h-12 w-12 text-secondary mx-auto mb-4" />
                <div className="text-4xl font-bold text-primary mb-2">500+</div>
                <p className="text-gray-600">Immobili venduti</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-center"
            >
              <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <Users className="h-12 w-12 text-secondary mx-auto mb-4" />
                <div className="text-4xl font-bold text-primary mb-2">98%</div>
                <p className="text-gray-600">Clienti soddisfatti</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-center"
            >
              <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <Star className="h-12 w-12 text-secondary mx-auto mb-4" />
                <div className="text-4xl font-bold text-primary mb-2">5/5</div>
                <p className="text-gray-600">Valutazione media</p>
              </div>
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-16 text-center"
          >
            <p className="text-gray-600 max-w-3xl mx-auto">
              Questi numeri sono il risultato della fiducia che i nostri clienti ripongono in noi ogni giorno. 
              La nostra missione è continuare a migliorare, offrendo un servizio immobiliare d'eccellenza a Scandicci.
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* DESIRE: Testimonials - rafforza il desiderio con il social proof */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="text-secondary uppercase tracking-wider font-semibold">Testimonianze</span>
            <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">
              Le Storie dei Nostri Clienti a Scandicci
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              Le parole di chi ha già scelto Molinaro Immobiliare per il proprio progetto immobiliare a Scandicci.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-gray-50 p-8 rounded-lg relative"
            >
              <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2 bg-secondary text-white w-10 h-10 rounded-full flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6">
                  <path d="M9.24 9.17C8.43 9.4 7.74 10.03 7.51 10.84C7.08 12.35 8.55 13.82 10.06 13.39C10.87 13.16 11.5 12.47 11.73 11.66C12.16 10.15 10.69 8.68 9.18 9.11L9.24 9.17ZM16.67 9.17C15.86 9.4 15.17 10.03 14.94 10.84C14.51 12.35 15.98 13.82 17.49 13.39C18.3 13.16 18.93 12.47 19.16 11.66C19.59 10.15 18.12 8.68 16.61 9.11L16.67 9.17Z" fill="currentColor"/>
                </svg>
              </div>
              
              <div className="flex space-x-1 mb-4 text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-current" />
                ))}
              </div>
              
              <p className="text-gray-600 mb-8 italic leading-relaxed">
                "La nostra esperienza con l'agenzia Molinaro immobiliare, è stata da 5 stelle, ricevendo un supporto professionale, completo e immediato da parte del titolare Federico. Ci siamo affidati a lui e possiamo dire con certezza che è una persona con un gran cuore, molto semplice, genuino e sincero, senza peli sulla lingua, con tanta esperienza e competenza sul campo, disponibile a chiarire qualsiasi dubbio e incertezze, seguendo e spiegando con molta professionalità, pazienza e sempre con il sorriso sulle labbra, tutti i passaggi burocratici che ad oggi ci hanno portato ad acquistare la casa dei nostri sogni."
              </p>
              
              <div className="flex items-center">
                <div>
                  <h4 className="font-semibold text-primary">Vincenzo Marotta</h4>
                  <p className="text-gray-500 text-sm">Scandicci</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-gray-50 p-8 rounded-lg relative"
            >
              <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2 bg-secondary text-white w-10 h-10 rounded-full flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6">
                  <path d="M9.24 9.17C8.43 9.4 7.74 10.03 7.51 10.84C7.08 12.35 8.55 13.82 10.06 13.39C10.87 13.16 11.5 12.47 11.73 11.66C12.16 10.15 10.69 8.68 9.18 9.11L9.24 9.17ZM16.67 9.17C15.86 9.4 15.17 10.03 14.94 10.84C14.51 12.35 15.98 13.82 17.49 13.39C18.3 13.16 18.93 12.47 19.16 11.66C19.59 10.15 18.12 8.68 16.61 9.11L16.67 9.17Z" fill="currentColor"/>
                </svg>
              </div>
              
              <div className="flex space-x-1 mb-4 text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-current" />
                ))}
              </div>
              
              <p className="text-gray-600 mb-8 italic leading-relaxed">
                "Professionisti seri e competenti, un'esperienza impeccabile. Mi sono rivolto all'agenzia "Molinaro Immobiliare" per la vendita della mia casa e sono rimasto estremamente soddisfatto del servizio ricevuto. L'agente, Federico, si è dimostrato fin da subito professionale, competente e disponibile, guidandomi con pazienza e chiarezza attraverso tutte le fasi della compravendita. Ha saputo valorizzare al meglio l'immobile, trovando rapidamente acquirenti interessati e gestendo la trattativa in modo impeccabile. La Molinaro Immobiliare si distingue per la serietà, la trasparenza e l'attenzione al cliente. Consiglio vivamente questa agenzia a chiunque debba vendere o acquistare casa."
              </p>
              
              <div className="flex items-center">
                <div>
                  <h4 className="font-semibold text-primary">Dario Galotti</h4>
                  <p className="text-gray-500 text-sm">2 mesi fa</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-gray-50 p-8 rounded-lg relative"
            >
              <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2 bg-secondary text-white w-10 h-10 rounded-full flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6">
                  <path d="M9.24 9.17C8.43 9.4 7.74 10.03 7.51 10.84C7.08 12.35 8.55 13.82 10.06 13.39C10.87 13.16 11.5 12.47 11.73 11.66C12.16 10.15 10.69 8.68 9.18 9.11L9.24 9.17ZM16.67 9.17C15.86 9.4 15.17 10.03 14.94 10.84C14.51 12.35 15.98 13.82 17.49 13.39C18.3 13.16 18.93 12.47 19.16 11.66C19.59 10.15 18.12 8.68 16.61 9.11L16.67 9.17Z" fill="currentColor"/>
                </svg>
              </div>
              
              <div className="flex space-x-1 mb-4 text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-current" />
                ))}
              </div>
              
              <p className="text-gray-600 mb-8 italic leading-relaxed">
                "L'agente immobiliare Federico ci ha seguito in tutto il processo di vendita con professionalità e serietà. Vi consigliamo vivamente questa agenzia che fa la differenze rispetto a molte altre!"
              </p>
              
              <div className="flex items-center">
                <div>
                  <h4 className="font-semibold text-primary">Giuseppina Pisano</h4>
                  <p className="text-gray-500 text-sm">2 settimane fa</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Location map section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="text-secondary uppercase tracking-wider font-semibold">La Nostra Sede</span>
            <h2 className="text-4xl font-monomakh text-primary mt-2 mb-4">
              Vieni a Trovarci a Scandicci
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              La nostra agenzia immobiliare è situata in una posizione strategica e facilmente accessibile
              nel cuore di Scandicci, perfetta per incontrarci e iniziare insieme il tuo progetto immobiliare.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-xl shadow-xl overflow-hidden"
          >
            <div className="grid md:grid-cols-5">
              <div className="md:col-span-3 h-[400px]">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2881.6!2d11.1786!3d43.7667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132a55d!2sVia+Pisana%2C+327%2C+50018+Scandicci+FI!5e0!3m2!1sen!2sit!4v1" 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sede Molinaro Immobiliare"
                ></iframe>
              </div>
              <div className="md:col-span-2 p-8 flex flex-col justify-center">
                <h3 className="text-2xl font-semibold text-primary mb-6">Molinaro Immobiliare</h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <MapPin className="h-6 w-6 text-secondary mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Indirizzo</p>
                      <p className="text-gray-600">Via Pisana 327, 50018 Scandicci (FI)</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Phone className="h-6 w-6 text-secondary mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Contatti</p>
                      <p className="text-gray-600">+39 327 8855115</p>
                      <p className="text-gray-600">055 752139</p>
                      <p className="text-gray-600">info@molinaroimmobiliare.it</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Clock className="h-6 w-6 text-secondary mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Orari di Apertura</p>
                      <p className="text-gray-600">Lun - Ven: 9:00 - 18:00</p>
                      <p className="text-gray-600">Sab: 9:00 - 12:30</p>
                      <p className="text-gray-600">Dom: Chiuso</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* ACTION: Contact Form - chiara call to action per convertire */}
      <section id="contattaci" className="py-24 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="" className="w-full h-full object-cover" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-xl overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="p-10">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <h2 className="text-3xl font-monomakh text-primary mb-6">Richiedi una Consulenza Gratuita</h2>
                  <p className="text-gray-600 mb-8">
                    Sei pronto a scoprire il valore del tuo immobile a Scandicci o a trovare 
                    la casa dei tuoi sogni? Compila il form e ti ricontatteremo entro 24 ore.
                  </p>
                  
                  <form className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nome e Cognome*</label>
                        <input 
                          type="text" 
                          required 
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Telefono*</label>
                        <input 
                          type="tel" 
                          required 
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email*</label>
                      <input 
                        type="email" 
                        required 
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sono interessato a</label>
                      <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary">
                        <option>Acquistare un immobile a Scandicci</option>
                        <option>Vendere un immobile a Scandicci</option>
                        <option>Affittare un immobile a Scandicci</option>
                        <option>Valutazione gratuita del mio immobile</option>
                        <option>Altro (specificare nel messaggio)</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Messaggio</label>
                      <textarea 
                        rows={4} 
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                      ></textarea>
                    </div>
                    
                    <div className="flex items-start">
                      <input 
                        type="checkbox" 
                        id="privacy" 
                        required
                        className="h-4 w-4 text-primary border-gray-300 rounded mt-1" 
                      />
                      <label htmlFor="privacy" className="ml-2 block text-xs text-gray-500">
                        Autorizzo il trattamento dei miei dati personali ai sensi del GDPR (UE 2016/679)
                      </label>
                    </div>
                    
                    <button
                      type="submit"
                      className="w-full bg-primary hover:bg-primary-light text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                    >
                      Invia Richiesta
                    </button>
                  </form>
                </motion.div>
              </div>
              
              <div className="hidden md:block">
                <img 
                  src="/images/agenzia5.jpeg" 
                  alt="Scandicci panorama" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AgenziaImmobiliareScandicci;